import Unlink20 from '@carbon/icons-react/es/unlink/20';
import { Tooltip } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import { SalesforceAPI } from 'api/integrations';
import { ReactComponent as SalesforceLogo } from 'assets/svgs/salesforce-logo.svg';
import { Loading } from 'components/atoms/loading';
import { Popconfirm } from 'components/atoms/popconfirm';
import { HideForCustomer } from 'components/atoms/user-type-utils';
import { IntegrationStatusTypes } from 'components/organisms/module-settings/integrations/integrations-list/authentication-types';
import { useTranslate } from 'hooks/use-translation';
import {
  SALESFORCE_GET_LINK_OBJECT_KEY,
  SALESFORCE_LINK_OBJECT_KEY,
  useSalesForceGetLinkObjectQuery,
  useSalesForceLinkObjectQuery,
} from 'queries/salesforce-integration';
import { ResponsiveCard } from '../../style';
import { SalesforceLinkObject } from './LinkSalesforceObjectForm';
import { SalesforceAuthExpire } from './salesforce-auth-expire';
import { SalesforceConnectedObject } from './salesforce-connected-object';

const Card = styled(ResponsiveCard)`
  max-height: unset;
  transition: height 0.5s linear;

  &:before {
    padding-bottom: 0%;
  }

  .ant-card-head {
    position: unset;
  }

  .ant-card-body {
    position: unset;
  }
`;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 54px;
    z-index: 0;
    background: var(--private-01);
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const Title = styled.strong`
  position: relative;
  bottom: var(--spacing-02);
`;

const SalesforceText = styled.span`
  margin-left: var(--spacing-02);
`;

const LoaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: var(--spacing-05);
`;

const StyledLink = styled.p`
  margin-top: var(--spacing-05);
  color: var(--blue-60);
  cursor: pointer;
`;

export const ProjectOverviewSalesforceCard: FC<{
  projectId: number;
  integrationId: number;
  integrationStatus: IntegrationStatusTypes;
  reauthenticateUrl?: string;
  linkedObjData: any;
  projectData: any;
  refetchObj: any;
  linkedObjDataStatus: any;
  oldIntegrationId: any;
}> = ({
  projectId,
  integrationId,
  integrationStatus,
  reauthenticateUrl,
  linkedObjData,
  projectData,
  linkedObjDataStatus,
  oldIntegrationId,
}) => {
  const [showLinkObj, setShowLinkObj] = useState(false);
  const [salesforceObj, setSalesforceObj] = useState<any>('');
  const handleClick = () => {
    setShowLinkObj(true);
  };
  const [unlinkLoading, setUnlinkLoading] = useState(false);
  const [errorState, setErrorState] = useState<any>('');

  const { status, error }: { status: string; error: any } =
    useSalesForceLinkObjectQuery(integrationId, projectId, salesforceObj, {
      enabled: salesforceObj !== '' && integrationId !== undefined,
    });

  const { status: linkedObjectStatus, data: objData } =
    useSalesForceGetLinkObjectQuery(
      projectData?.[0]?.integrationId,
      projectData?.[0]?.externalResourceId,
      projectData?.[0]?.externalResourceType,
      {
        enabled: status === 'success',
      },
    );
  const [
    unlinkConfirmationModalVisibility,
    setUnlinkConfirmationModalVisibility,
  ] = useState(false);

  const queryClient = useQueryClient();
  const translate = useTranslate();
  const unlinkTitle = translate('unlink');

  useEffect(() => {
    setErrorState(error);
  }, [error]);

  useEffect(() => {
    return () => {
      queryClient.removeQueries();
    };
  }, [queryClient]);

  useEffect(() => {
    if (status === 'success') {
      queryClient.removeQueries();
    }
  }, [status, queryClient]);

  useEffect(() => {
    queryClient.removeQueries(SALESFORCE_GET_LINK_OBJECT_KEY);
  }, [queryClient]);

  const handleUnlink = () => {
    setUnlinkConfirmationModalVisibility(false);
    setUnlinkLoading(true);
    SalesforceAPI.unLinkSalesforceObjectWithProject(
      oldIntegrationId,
      projectData?.resourceMappingId,
    ).then(() => {
      queryClient.setQueriesData(SALESFORCE_GET_LINK_OBJECT_KEY, undefined);
      setUnlinkLoading(false);
    });
  };

  const linkSalesforceObject = (url) => {
    queryClient.removeQueries(SALESFORCE_LINK_OBJECT_KEY);
    if (url !== '') {
      setSalesforceObj(url);
    }
  };

  if (integrationStatus === IntegrationStatusTypes.Expired) {
    return (
      <Card
        title={
          <Header>
            <SalesforceLogo />
            <strong>
              <FormattedMessage id="settings.integrations.sf.title" />
            </strong>
          </Header>
        }
      >
        {reauthenticateUrl && (
          <SalesforceAuthExpire reauthenticateUrl={reauthenticateUrl} />
        )}
      </Card>
    );
  }

  return (
    <HideForCustomer>
      <Card
        title={
          <Header>
            <Title>
              <SalesforceLogo
                style={{ width: 24, height: 24, position: 'relative', top: 8 }}
              />
              <SalesforceText>
                <FormattedMessage id="settings.integrations.sf.title" />
              </SalesforceText>
            </Title>
            {(objData || linkedObjData) &&
              (!unlinkConfirmationModalVisibility ? (
                <Tooltip
                  title={
                    <FormattedMessage id="project.overview.unlink_a_salesforce_object" />
                  }
                >
                  <Unlink20
                    style={{ color: 'red', cursor: 'pointer' }}
                    onClick={() => setUnlinkConfirmationModalVisibility(true)}
                  />
                </Tooltip>
              ) : (
                <Popconfirm
                  visible={unlinkConfirmationModalVisibility}
                  title={unlinkTitle}
                  subTitle="Are you sure, you want to unlink this object?"
                  onConfirm={handleUnlink}
                  onCancel={() => setUnlinkConfirmationModalVisibility(false)}
                  ctaButtonText="Unlink"
                >
                  <div>
                    <Unlink20
                      style={{ color: 'red', cursor: 'pointer' }}
                      onClick={() => setUnlinkConfirmationModalVisibility(true)}
                    />
                  </div>
                </Popconfirm>
              ))}
          </Header>
        }
      >
        {unlinkLoading === true ||
        status === 'loading' ||
        linkedObjectStatus === 'loading' ? (
          <LoaderWrapper>
            <Loading />
          </LoaderWrapper>
        ) : linkedObjData ? (
          <SalesforceConnectedObject
            object={linkedObjData.type}
            name={linkedObjData.name}
            url={linkedObjData.url}
          />
        ) : objData ? (
          <SalesforceConnectedObject
            object={objData.type}
            name={objData.name}
            url={objData.url}
          />
        ) : !showLinkObj ? (
          <StyledLink onClick={handleClick}>
            <FormattedMessage id="project.overview.link_a_salesforce_object" />
          </StyledLink>
        ) : !linkedObjData && linkedObjDataStatus === 'loading' ? (
          <Loading />
        ) : (
          <SalesforceLinkObject
            handleSubmit={linkSalesforceObject}
            setErrorState={setErrorState}
            errorState={errorState}
            onClose={() => setShowLinkObj(false)}
            error={error?.response?.data?.errors[0].reason}
          />
        )}
      </Card>
    </HideForCustomer>
  );
};
